import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { Mail, Save, ArrowLeft, Info, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.typography.fontSize['2xl']};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0 0;
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const SettingsCard = styled(Card)`
  padding: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1.5px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-family: ${p => p.theme.typography.fontFamily.primary};
  min-height: 150px;
  resize: vertical;
  line-height: 1.6;
  
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.muted};
  }
`;

const HelpText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${p => p.theme.colors.primary}10;
  border-left: 3px solid ${p => p.theme.colors.primary};
  border-radius: 4px;
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.5;
`;

const TokenList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Token = styled.code`
  padding: 0.25rem 0.5rem;
  background: ${p => p.theme.colors.background.secondary};
  border: 1px solid ${p => p.theme.colors.border.light};
  border-radius: 4px;
  font-size: 0.8rem;
  color: ${p => p.theme.colors.primary};
  font-family: 'Courier New', monospace;
`;

const PreviewSection = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.lg};
  border: 1px solid ${p => p.theme.colors.border.light};
`;

const PreviewTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: ${p => p.theme.typography.fontSize.lg};
  color: ${p => p.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PreviewContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border.light};
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #111;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const TrainingEmailTemplates = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState({
    subject: 'Congratulations! You completed {{training_title}}',
    greeting: 'Dear {{participant_name}},',
    body: 'We are delighted to inform you that you have successfully completed {{training_title}}.',
    details_intro: 'Training Details:',
    next_steps_title: 'What\'s Next?',
    next_steps: '• Your completion has been recorded in our system\n• You can now access this training in your member profile\n• Look out for future advanced training opportunities\n• Share your experience with fellow network members',
    closing: 'Thank you for your active participation and commitment to advancing human rights in Ethiopia. Your dedication makes a real difference in our communities.',
    signature: 'Best regards,\n{{organization_name}} Team\nEmpowering Women Human Rights Defenders Network',
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/training-email-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTemplate(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('No custom template found, using defaults');
    }
  };

  const handleChange = (field, value) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/training-email-template`, template, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email template saved successfully!');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.response?.data?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const availableTokens = [
    { token: '{{participant_name}}', description: 'Participant\'s full name' },
    { token: '{{training_title}}', description: 'Training program title' },
    { token: '{{training_dates}}', description: 'Training start and end dates' },
    { token: '{{training_location}}', description: 'Training location' },
    { token: '{{trainer_name}}', description: 'Trainer\'s name' },
    { token: '{{completion_date}}', description: 'Date training was completed' },
    { token: '{{organization_name}}', description: 'Your organization name' },
  ];

  const renderPreview = () => {
    const sampleData = {
      '{{participant_name}}': 'Jane Doe',
      '{{training_title}}': 'Digital Security Training',
      '{{training_dates}}': 'January 10, 2025 - January 15, 2025',
      '{{training_location}}': 'Addis Ababa',
      '{{trainer_name}}': 'Dr. Smith',
      '{{completion_date}}': 'January 15, 2025',
      '{{organization_name}}': 'EWHRDN',
    };

    const replaceTokens = (text) => {
      let result = text;
      Object.entries(sampleData).forEach(([token, value]) => {
        result = result.replace(new RegExp(token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
      });
      return result;
    };

    return (
      <PreviewContent>
        <h2 style={{ color: '#c11f52', margin: '0 0 1rem 0' }}>
          {replaceTokens(template.subject)}
        </h2>
        <p>{replaceTokens(template.greeting)}</p>
        <p>{replaceTokens(template.body)}</p>
        <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '4px', margin: '1rem 0' }}>
          <strong>{template.details_intro}</strong>
          <div style={{ marginTop: '0.5rem' }}>
            <div>Training: {replaceTokens('{{training_title}}')}</div>
            <div>Dates: {replaceTokens('{{training_dates}}')}</div>
            <div>Location: {replaceTokens('{{training_location}}')}</div>
            <div>Trainer: {replaceTokens('{{trainer_name}}')}</div>
            <div>Completion: {replaceTokens('{{completion_date}}')}</div>
          </div>
        </div>
        <div style={{ background: '#ecfdf5', padding: '1rem', borderRadius: '4px', margin: '1rem 0' }}>
          <strong>{template.next_steps_title}</strong>
          <div style={{ marginTop: '0.5rem', whiteSpace: 'pre-line' }}>{template.next_steps}</div>
        </div>
        <p>{replaceTokens(template.closing)}</p>
        <p style={{ marginTop: '2rem', whiteSpace: 'pre-line' }}>{replaceTokens(template.signature)}</p>
      </PreviewContent>
    );
  };

  return (
    <PageContainer>
      <Header>
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/training/manage')}
            leftIcon={<ArrowLeft size={16} />}
          >
            Back to Training Management
          </Button>
          <Title theme={theme}>
            <Mail size={28} />
            Training Completion Email Template
          </Title>
          <Subtitle theme={theme}>
            Customize the email sent to participants when they complete a training
          </Subtitle>
        </div>
      </Header>

      <SettingsCard theme={theme}>
        <FormGroup>
          <Label theme={theme}>
            Email Subject
          </Label>
          <Input
            value={template.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            placeholder="Congratulations! You completed {{training_title}}"
          />
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Greeting
          </Label>
          <Input
            value={template.greeting}
            onChange={(e) => handleChange('greeting', e.target.value)}
            placeholder="Dear {{participant_name}},"
          />
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Main Message
          </Label>
          <Textarea
            theme={theme}
            value={template.body}
            onChange={(e) => handleChange('body', e.target.value)}
            placeholder="We are delighted to inform you that you have successfully completed {{training_title}}."
          />
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Details Section Title
          </Label>
          <Input
            value={template.details_intro}
            onChange={(e) => handleChange('details_intro', e.target.value)}
            placeholder="Training Details:"
          />
          <HelpText theme={theme}>
            <Info size={16} />
            This appears above the training details table (dates, location, trainer, etc.)
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Next Steps Title
          </Label>
          <Input
            value={template.next_steps_title}
            onChange={(e) => handleChange('next_steps_title', e.target.value)}
            placeholder="What's Next?"
          />
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Next Steps Content
          </Label>
          <Textarea
            theme={theme}
            value={template.next_steps}
            onChange={(e) => handleChange('next_steps', e.target.value)}
            placeholder="• Your completion has been recorded..."
            rows={5}
          />
          <HelpText theme={theme}>
            <Info size={16} />
            Use bullet points (•) or line breaks for better formatting
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Closing Message
          </Label>
          <Textarea
            theme={theme}
            value={template.closing}
            onChange={(e) => handleChange('closing', e.target.value)}
            placeholder="Thank you for your active participation..."
          />
        </FormGroup>

        <FormGroup>
          <Label theme={theme}>
            Email Signature
          </Label>
          <Textarea
            theme={theme}
            value={template.signature}
            onChange={(e) => handleChange('signature', e.target.value)}
            placeholder="Best regards,\n{{organization_name}} Team"
            rows={4}
          />
        </FormGroup>

        <HelpText theme={theme}>
          <Info size={16} />
          <div>
            <strong>Available Tokens:</strong>
            <TokenList>
              {availableTokens.map(({ token, description }) => (
                <div key={token} style={{ marginTop: '0.25rem' }}>
                  <Token theme={theme}>{token}</Token> - {description}
                </div>
              ))}
            </TokenList>
          </div>
        </HelpText>

        <ButtonGroup>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/training/manage')}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<Save size={16} />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Template'}
          </Button>
        </ButtonGroup>
      </SettingsCard>

      <PreviewSection theme={theme}>
        <PreviewTitle theme={theme}>
          <Eye size={20} />
          Email Preview
        </PreviewTitle>
        {renderPreview()}
      </PreviewSection>
    </PageContainer>
  );
};

export default TrainingEmailTemplates;

